import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { getLoaderBtn, getModalSwitch } from '@app/store/actions';
import { ModalActionsComponent, PasslyTextField } from '@app/components';
import { FormControl } from '@material-ui/core';
import { onPressEnterModal, genericErrorToast } from '@app/helpers';
import { vaultFoldersServiceNew } from '@app/services';
import { PasslyAutoComplete } from '@app/componentsV2';
import { IVaultItem } from '@app/pages/passwordManager/interfaces';

export interface IReceivedData {
    handleRefresh: Function;
    allVaults: IVaultItem[];
}

export const AddFolderDialog = ({ handleRefresh, allVaults }: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [folderName, setFolderName] = useState('');
    const [folderNameError, setFolderNameError] = useState('');
    const [folderVaultId, setFolderVaultId] = useState<any>(null);
    const [vaultsAutocompleteItems, setVaultsAutocompleteItems] = useState<any>(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(!folderName || !folderVaultId);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        handleAllVaultsSuccess(allVaults);
    }, [allVaults]);

    useEffect(() => {
        let disabled;
        let errMessage = '';

        const chosenVault = allVaults.find((vault) => vault.id === folderVaultId);
        const isFolderLimitReached = chosenVault && chosenVault.folders?.length >= 10;

        if (isFolderLimitReached) {
            errMessage = translation('FOLDERS_QUANTITY_ERROR');
            disabled = true;
        } else {
            disabled = !folderName || !folderVaultId;
        }

        if (folderName && folderVaultId && !isFolderNameUnique(folderName)) {
            setFolderNameError(translation('FOLDER_NAME_UNIQUENESS_ERROR'));
            disabled = true;
        }

        setErrorMessage(errMessage);
        setIsSubmitDisabled(disabled);
    }, [folderName, folderVaultId]);

    const handleAllVaultsSuccess = (values: IVaultItem[]) => {
        const resultValues = values.map((value) => {
            return { name: value.displayName, code: value.id };
        });

        setVaultsAutocompleteItems(resultValues);
    };

    const isFolderNameUnique = (folderName: string) => {
        const vault = allVaults.find((vault) => vault.id === folderVaultId);
        const folders = vault && vault.folders;

        return folders?.findIndex((folder) => folder.displayName === folderName) === -1;
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'folderName':
                setFolderName(value);
                setFolderNameError(!value ? translation('REQUIRED_FIELD') : '');
                break;
            case 'folderVault':
                setFolderVaultId(value.code);
                break;
            default:
                break;
        }
    };

    const handleCancel = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
    };

    const handleSaveBtn = () => {
        dispatch(getLoaderBtn(true));

        let jsonObject: any = {};

        jsonObject.displayName = folderName;

        vaultFoldersServiceNew.addVaultFolder(folderVaultId, jsonObject, handleError, handleSuccess);
    };

    const handleError = (response: any) => {
        dispatch(getLoaderBtn(false));

        genericErrorToast(response);
    };

    const handleSuccess = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
        handleRefresh(true);
    };

    return (
        <>
            <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !isSubmitDisabled)}>
                <>
                    <form method='post'>
                        <div className='modal__body--row--space'>
                            <FormControl>
                                <PasslyTextField
                                    name='folderName'
                                    label={'FOLDER_NAME'}
                                    value={folderName}
                                    onChange={onFormChange}
                                    id='folderName-input'
                                    inputProps={{ maxLength: 255 }}
                                    autoFocus
                                    required
                                    error={!!folderNameError}
                                    helperText={folderNameError}
                                />
                            </FormControl>
                        </div>

                        <div className='modal__body--row--space'>
                            <PasslyAutoComplete
                                className='folderVault'
                                items={vaultsAutocompleteItems}
                                onChange={onFormChange}
                                id='folderVault-input'
                                name='folderVault'
                                label={translation('VAULT')}
                                value={folderVaultId}
                                dropdown={true}
                                appendTo={document.body}
                                required
                            />
                        </div>
                    </form>
                </>

                {errorMessage && <div className='modal__error'>{errorMessage}</div>}
            </div>

            <ModalActionsComponent
                primaryBtn={translation('ADD_VAULT_FOLDER')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={isSubmitDisabled}
                id='addVault'
            />
        </>
    );
};
